/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t, Trans } from '@lingui/macro'
import { Close } from '@mui/icons-material'
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputLabel,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography
} from '@mui/material'
import { formatDate } from '@om1/platform-components'
import { debounce } from 'lodash'
import { FileText, User } from 'lucide-react'
import { singular } from 'pluralize'
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ValidColumn } from '../components/GenericDetailTable'
export type GenericDataPageProps<T extends { [key in K]: string | boolean }, K extends ValidColumn<T>, Q = {}> = {
    useData: () => {
        response: { data: T[]; meta: { totalItems: number } } | null
        loading: boolean
        error: string | null
        page: number
        setPage: (page: number) => void
        limit: number
        setLimit: (limit: number) => void
        sort: string
        setSort: (sort: string) => void
        create?: (requestBody: Q) => Promise<T | {}> | ((requestBody: {}) => Promise<{}>)
        search?: string
        setSearch: (search: string) => void
        filters?: Record<string, string>
        setFilters: (filters: Record<string, string>) => void
        refetch: () => void
    }
    useDataArgs?: Record<string, any>
    columns: Column<T>[]
    customColumns?: CustomColumn<T>[]
    title: string
    detailPageRoute?: (id: string) => string
    orientation?: 'vertical' | 'standard'
    iconResolver?: (item: T) => JSX.Element
    iconTitle?: string
    actions?: {
        label: string
        icon?: React.ReactNode
        onClick: (item: T) => void
    }[]
    filterButtons?: (props: {
        onSearchChange: (search: string) => void
        currentSearch: string
        onFilterChange: (filters: Record<string, string>) => void
        currentFilters: Record<string, string>
        ref?: React.RefObject<HTMLDivElement> | undefined
    }) => ReactNode
    additionalActionButtons?: React.ReactNode
    filters?: Record<string, string>
    search?: string
    singularTitle?: string
    canCreate?: boolean
    organizationContext?: {
        organizationId: string
        referrerId?: string
    }
}

export type Column<T> = {
    key: ValidColumn<T>
    label?: string
    type: React.InputHTMLAttributes<unknown>['type'] | 'checkbox' | 'select'
    editable?: boolean
    required?: boolean
    disableSort?: boolean
    visible?: boolean // Whether to show this column in the table (default: true)
    displayValue?: (value: string) => string | ReactNode
    /**
     * Optional sub-values to display below the main column value.
     * This allows any column to show additional information like:
     * - Status indicators (SMS consent, language preference)
     * - Metadata (last updated, verification status)
     * - Related data (care partner info, notes)
     *
     * @param item - The full data item for the row
     * @returns ReactNode to render below the main column value
     *
     * @example
     * // Phone column with SMS consent and language preference
     * {
     *     key: 'phone',
     *     type: 'tel',
     *     subValues: (candidate) => (
     *         <div className='text-[8px]'>
     *             SMS: {candidate.smsConsent ? '✓' : '✗'}
     *             Lang: {candidate.languagePreference === 'es' ? 'ES' : 'EN'}
     *         </div>
     *     )
     * }
     */
    subValues?: (item: T) => ReactNode
}

type CustomColumn<T> = {
    label: string
    value: ((item: T, refetch?: () => void) => ReactNode) | ReactNode
}

export const defaultLabel = <T extends Record<string, unknown>>(field: ValidColumn<T>) => {
    const parts = String(field).split('.')

    // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
    return parts
        .map((part) =>
            part
                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
                .replace('Dttm', '') // Remove Dttm suffix
                .trim()
        )
        .join(' ')
}

// First, create a memoized filter buttons wrapper
const FilterButtonsWrapper = React.memo<{
    filterButtons: GenericDataPageProps<any, any>['filterButtons']
    onSearchChange: (search: string) => void
    currentSearch?: string
    filters?: Record<string, string>
    onFilterChange?: (filters: Record<string, string>) => void
}>(
    ({ filterButtons, onSearchChange, currentSearch, filters, onFilterChange }) => {
        const content = filterButtons?.({
            onSearchChange,
            currentSearch: currentSearch || '',
            onFilterChange: onFilterChange || (() => {}),
            currentFilters: filters || {}
        })

        // Ensure we return a ReactElement or null
        return content ? <>{content}</> : null
    },
    (prev, next) => {
        return prev.currentSearch === next.currentSearch && prev.onSearchChange === next.onSearchChange
    }
)

FilterButtonsWrapper.displayName = 'FilterButtonsWrapper'

// Create a completely separate component for the header section
const HeaderSection = React.memo<{
    title: string
    singularTitle?: string
    totalItems?: number
    error?: string | null
    filterButtons?: GenericDataPageProps<any, any>['filterButtons']
    canCreate: boolean
    handleDialogOpen: () => void
    onSearchChange: (search: string) => void
    currentSearch?: string
    onFilterChange?: (filters: Record<string, string>) => void
    currentFilters?: Record<string, string>
    additionalActionButtons?: React.ReactNode
}>(
    ({
        title,
        singularTitle,
        totalItems,
        error,
        filterButtons,
        canCreate,
        handleDialogOpen,
        onSearchChange,
        currentSearch,
        onFilterChange,
        currentFilters,
        additionalActionButtons
    }) => {
        // Render filter buttons directly here instead of in the parent
        const filterButtonsContent = filterButtons
            ? filterButtons({
                  onSearchChange,
                  currentSearch: currentSearch || '',
                  onFilterChange: onFilterChange || (() => {}),
                  currentFilters: currentFilters || {}
              })
            : null

        return (
            <div className='flex justify-between items-center mb-6 flex-shrink-0'>
                <div className='flex items-center'>
                    <div className='bg-primary-main text-white p-1.5 rounded-full flex-shrink-0 mr-3'>
                        {title === 'Patient List' && <User size={20} />}
                        {title === 'Patient Tasks' && <FileText size={20} />}
                        {title === 'Candidate Worklist' && <User size={20} />}
                        {title === 'Providers' && <User size={20} />}
                    </div>
                    <div>
                        <h1 className='text-2xl font-bold text-gray-800'>{title}</h1>
                        <div className='text-sm text-gray-500 mt-1'>
                            {typeof totalItems === 'number' && (
                                <span>
                                    Total: {totalItems}{' '}
                                    {totalItems === 1 ? singularTitle || singular(title) : singularTitle ? singularTitle + 's' : title}
                                </span>
                            )}
                        </div>
                    </div>
                    {error && (
                        <Typography color='error' className='ml-4 text-sm'>
                            {error}
                        </Typography>
                    )}
                </div>

                {/* Center: Filters */}
                <div className='flex-1 flex justify-center px-8'>{filterButtonsContent}</div>

                <div className='flex items-center gap-4'>
                    {additionalActionButtons}
                    {canCreate && (
                        <button
                            onClick={handleDialogOpen}
                            className='flex items-center justify-center gap-1.5 px-4 py-2 bg-primary-main text-white rounded-md shadow-sm hover:bg-primary-dark transition-colors text-sm font-medium min-w-[120px] h-[36px]'
                        >
                            <span>
                                + <Trans>Create {singularTitle ? singularTitle : singular(title)}</Trans>
                            </span>
                        </button>
                    )}
                </div>
            </div>
        )
    },
    (prevProps, nextProps) => {
        // Custom comparison to prevent unnecessary rerenders
        return (
            prevProps.title === nextProps.title &&
            prevProps.singularTitle === nextProps.singularTitle &&
            prevProps.totalItems === nextProps.totalItems &&
            prevProps.error === nextProps.error &&
            prevProps.canCreate === nextProps.canCreate &&
            prevProps.filterButtons === nextProps.filterButtons &&
            prevProps.handleDialogOpen === nextProps.handleDialogOpen &&
            prevProps.onSearchChange === nextProps.onSearchChange
        )
    }
)

HeaderSection.displayName = 'HeaderSection'

// Add this new component at the top of the file
const StableSearchInput = React.memo<{
    onSearchChange: (search: string) => void
    initialValue?: string
}>(({ onSearchChange, initialValue = '' }) => {
    // Maintain internal state
    const [value, setValue] = useState(initialValue)
    const inputRef = useRef<HTMLInputElement>(null)

    const debouncedSearchChange = useMemo(
        () =>
            debounce((newSearch: string) => {
                onSearchChange(newSearch)
            }, 300),
        [onSearchChange]
    )

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            setValue(newValue)
            debouncedSearchChange(newValue)
        },
        [debouncedSearchChange]
    )

    const handleClear = useCallback(() => {
        setValue('')
        debouncedSearchChange('')
        inputRef.current?.focus()
    }, [debouncedSearchChange])

    // Cleanup
    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel()
        }
    }, [debouncedSearchChange])

    return (
        <TextField
            label={t`Filter`}
            value={value}
            onChange={handleChange}
            size='small'
            className='rounded-md bg-white'
            inputRef={inputRef}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                }
            }}
            inputProps={{
                'data-testid': 'source-filter-input'
            }}
            InputProps={{
                endAdornment: value && (
                    <IconButton
                        aria-label='clear'
                        data-testid='clear-filter-button'
                        onClick={handleClear}
                        size='small'
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <Close fontSize='small' />
                    </IconButton>
                )
            }}
        />
    )
})

StableSearchInput.displayName = 'StableSearchInput'

// Create a memoized component for the data view
const MemoizedDataView = React.memo<{
    response: { data: any[]; meta: { totalItems: number } } | null
    orientation: 'vertical' | 'standard'
    columns: Column<any>[]
    customColumns?: CustomColumn<any>[]
    iconResolver?: (item: any) => JSX.Element
    handleRowClick: (item: any) => void
    page: number
    limit: number
    handlePageChange: (event: unknown, newPage: number) => void
    handleRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    sort: string
    handleSortChange: (property: any) => void
    disableSort: (key: any) => boolean
    actions?: { label: string; icon?: React.ReactNode; onClick: (item: any) => void }[]
    iconTitle?: string
    singularTitle?: string
    title: string
    refetch: () => void
}>(
    ({
        response,
        orientation,
        columns,
        customColumns,
        iconResolver,
        handleRowClick,
        page,
        limit,
        handlePageChange,
        handleRowsPerPageChange,
        sort,
        handleSortChange,
        disableSort,
        actions,
        iconTitle,
        singularTitle,
        title,
        refetch
    }) => {
        const renderStandardTable = () => {
            return (
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {iconTitle && iconResolver && (
                                <TableCell
                                    className='border-b border-gray-200 text-sm font-medium bg-gray-50 w-[120px]'
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                        backgroundColor: 'rgb(249 250 251)',
                                        borderBottom: '1px solid rgb(229 231 235)'
                                    }}
                                >
                                    <TableSortLabel
                                        active={sort.startsWith(toSnakeCase(String('greenlightId')))}
                                        direction={sort.endsWith('asc') ? 'asc' : 'desc'}
                                        onClick={() => handleSortChange('greenlightId')}
                                        className='font-semibold'
                                    >
                                        {iconTitle}
                                    </TableSortLabel>
                                </TableCell>
                            )}

                            {columns
                                .filter((column) => column.visible !== false) // Default to visible if not specified
                                .map((column) => (
                                    <TableCell
                                        key={String(column.key)}
                                        className='border-b border-gray-200 text-sm font-medium bg-gray-50'
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 10,
                                            backgroundColor: 'rgb(249 250 251)',
                                            borderBottom: '1px solid rgb(229 231 235)'
                                        }}
                                    >
                                        <TableSortLabel
                                            active={sort.startsWith(toSnakeCase(String(column.key)))}
                                            direction={sort.endsWith('asc') ? 'asc' : 'desc'}
                                            onClick={() => handleSortChange(column.key)}
                                            className='font-semibold'
                                            disabled={disableSort(column.key)}
                                        >
                                            {column.label ? column.label : defaultLabel(String(column.key))}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            {customColumns &&
                                customColumns.map((column) => (
                                    <TableCell
                                        key={column.label}
                                        className='border-b border-gray-200 text-sm font-medium bg-gray-50'
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 10,
                                            backgroundColor: 'rgb(249 250 251)',
                                            borderBottom: '1px solid rgb(229 231 235)'
                                        }}
                                    >
                                        <span className='font-semibold'>{column.label}</span>
                                    </TableCell>
                                ))}
                            {actions &&
                                actions.map((action) => (
                                    <TableCell key={action.label} className='border-b border-gray-200 text-sm'>
                                        <span className='font-semibold'>{action.icon}</span>
                                    </TableCell>
                                ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {response!.data.map((item, index) => (
                            <TableRow key={index} hover className='cursor-pointer hover:bg-gray-50'>
                                {iconResolver && (
                                    <TableCell className='border-b border-gray-200 text-sm w-[120px]' onClick={() => handleRowClick(item)}>
                                        {iconResolver(item)}
                                    </TableCell>
                                )}
                                {columns
                                    .filter((column) => column.visible !== false) // Default to visible if not specified
                                    .map((column, index) => {
                                        const value = getValueFromColumn(item, column)
                                        return (
                                            <TableCell
                                                key={String(column.key)}
                                                onClick={() => handleRowClick(item)}
                                                className='border-b border-gray-200 text-sm'
                                            >
                                                {column.displayValue
                                                    ? column.displayValue(value)
                                                    : column.type === 'date'
                                                    ? formatDate(String(value))
                                                    : String(value ? value : 'N/A')}
                                                {/* Flexible sub-value system - any column can define its own sub-values */}
                                                {column.subValues && column.subValues(item)}
                                            </TableCell>
                                        )
                                    })}
                                {customColumns &&
                                    customColumns.map((column) => (
                                        <TableCell key={column.label} className='border-b border-gray-200 text-sm'>
                                            {typeof column.value === 'function' ? column.value(item, refetch) : column.value}
                                        </TableCell>
                                    ))}
                                {actions &&
                                    actions.map((action) => (
                                        <TableCell
                                            key={action.label}
                                            onClick={() => action.onClick(item)}
                                            className='border-b border-gray-200 text-sm'
                                        >
                                            <button className='w-full text-center'>{action.icon}</button>
                                        </TableCell>
                                    ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )
        }

        const renderVerticalTable = () => {
            return (
                <Table>
                    <TableBody>
                        {response!.data.map((item, rowIndex) => (
                            <TableRow key={rowIndex} hover className='cursor-pointer hover:bg-gray-50'>
                                {iconResolver && <TableCell className='border-b border-gray-200 text-sm'>{iconResolver(item)}</TableCell>}
                                {columns.map((column, colIndex) => {
                                    const value = getValueFromColumn(item, column)
                                    return (
                                        <TableRow
                                            key={colIndex}
                                            onClick={() => handleRowClick(item)}
                                            hover
                                            className='cursor-pointer hover:bg-gray-50'
                                        >
                                            <TableCell className='font-bold border-b border-gray-200 text-sm' sx={{ fontWeight: 'bold' }}>
                                                {defaultLabel(String(column.key))}
                                            </TableCell>
                                            <TableCell className='border-b border-gray-200 text-sm'>
                                                {column.type === 'date' ? formatDate(String(value)) : String(value ? value : 'N/A')}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                {customColumns &&
                                    customColumns.map((column, idx) => (
                                        <TableRow
                                            key={`custom-${idx}`}
                                            onClick={() => handleRowClick(item)}
                                            hover
                                            className='cursor-pointer hover:bg-gray-50'
                                        >
                                            <TableCell className='font-bold border-b border-gray-200 text-sm' sx={{ fontWeight: 'bold' }}>
                                                {column.label}
                                            </TableCell>
                                            <TableCell className='border-b border-gray-200 text-sm'>
                                                {typeof column.value === 'function' ? column.value(item, refetch) : column.value}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )
        }

        if (!response || response.data.length === 0) {
            return (
                <div className='p-8 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-500'>
                    <Trans>No {singularTitle ? singularTitle : singular(title)} found</Trans>
                </div>
            )
        }

        return (
            <div className='flex flex-col h-full'>
                <div className='flex-grow mb-4' style={{ overflow: 'hidden' }}>
                    <TableContainer
                        component={Paper}
                        className='border border-gray-200 rounded-lg shadow-sm h-full'
                        style={{
                            maxHeight: 'calc(100vh - 250px)',
                            overflow: 'auto'
                        }}
                    >
                        {orientation === 'standard' ? renderStandardTable() : renderVerticalTable()}
                    </TableContainer>
                </div>
                <div className='flex-shrink-0'>
                    <TablePagination
                        component={Paper}
                        count={response!.meta.totalItems}
                        page={page - 1}
                        onPageChange={handlePageChange}
                        rowsPerPage={limit}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        className='bg-white border border-gray-200 rounded-lg shadow-sm w-full'
                        labelRowsPerPage={t`Items per page:`}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t`of`} ${count}`}
                    />
                </div>
            </div>
        )
    },
    (prevProps, nextProps) => {
        // Deep compare the data arrays
        const prevData = prevProps.response?.data
        const nextData = nextProps.response?.data

        if (prevData && nextData) {
            // If array lengths differ, data has changed
            if (prevData.length !== nextData.length) return false

            // Compare each item in the arrays
            for (let i = 0; i < prevData.length; i++) {
                const prevItem = prevData[i]
                const nextItem = nextData[i]

                // Compare all keys in the items
                for (const key in prevItem) {
                    if (prevItem[key] !== nextItem[key]) {
                        return false // Data has changed
                    }
                }
            }
        } else if (prevData !== nextData) {
            return false // One is null and the other isn't
        }

        // Compare other essential props
        return (
            prevProps.page === nextProps.page &&
            prevProps.limit === nextProps.limit &&
            prevProps.sort === nextProps.sort &&
            // Remove refetch from comparison since it's a function
            JSON.stringify(prevProps.response?.meta) === JSON.stringify(nextProps.response?.meta)
        )
    }
)

MemoizedDataView.displayName = 'MemoizedDataView'

// Create a memoized component for the loading state
const LoadingView = React.memo(() => (
    <div className='flex justify-center items-center h-[calc(100vh-100px)]' data-testid='loading-indicator'>
        <CircularProgress className='animate-spin' size={40} />
    </div>
))

LoadingView.displayName = 'LoadingView'

// Create a memoized component for the dialog
const CreateDialog = React.memo<{
    open: boolean
    onClose: () => void
    columns: Column<any>[]
    newObject: any
    errors: any
    handleInputChange: (key: any, value: string | boolean) => void
    handleCreate: () => void
    title: string
    singularTitle?: string
    focusedField: any
    setFocusedField: (field: any) => void
}>(({ open, onClose, columns, newObject, errors, handleInputChange, handleCreate, title, singularTitle, focusedField, setFocusedField }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle className='bg-gray-50 border-b border-gray-200'>
            <Trans>Create {singularTitle ? singularTitle : singular(title)}</Trans>
        </DialogTitle>
        <DialogContent className='pt-4'>
            {columns
                .filter((column) => column.editable)
                .map((column) => {
                    if (column.type === 'checkbox') {
                        return (
                            <FormControl key={String(column.key)} fullWidth margin='normal'>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!newObject[column.key]}
                                            onChange={(e) => handleInputChange(column.key, e.target.checked)}
                                        />
                                    }
                                    label={column.label || defaultLabel(String(column.key))}
                                />
                                {errors[column.key] && <FormHelperText>{errors[column.key]}</FormHelperText>}
                            </FormControl>
                        )
                    } else if (column.type === 'select') {
                        return (
                            <FormControl key={String(column.key)} fullWidth margin='normal' error={!!errors[column.key]}>
                                <InputLabel
                                    id={`${String(column.key)}-label`}
                                    shrink
                                    sx={{
                                        backgroundColor: 'white',
                                        padding: '0 4px',
                                        transform: 'translate(14px, -6px) scale(0.75)',
                                        color: errors[column.key] ? '#d32f2f' : 'inherit'
                                    }}
                                >
                                    {column.label || defaultLabel(String(column.key))}
                                </InputLabel>
                                <Select
                                    native
                                    error={!!errors[column.key]}
                                    labelId={`${String(column.key)}-label`}
                                    value={newObject[column.key] || ''}
                                    onChange={(e) => handleInputChange(column.key, e.target.value as string)}
                                    label={column.label || defaultLabel(String(column.key))}
                                    inputProps={{
                                        'aria-labelledby': `${String(column.key)}-label`
                                    }}
                                >
                                    <option aria-label='None' value='' />
                                    {column.key === 'languagePreference' ? (
                                        <>
                                            <option value='en' selected>
                                                English
                                            </option>
                                            <option value='es'>Spanish</option>
                                        </>
                                    ) : (
                                        <option value=''>Select...</option>
                                    )}
                                </Select>
                                {errors[column.key] && <FormHelperText>{errors[column.key]}</FormHelperText>}
                            </FormControl>
                        )
                    } else if (defaultLabel(String(column.key)) === 'Sex' || column.key === 'sex') {
                        return (
                            <FormControl key={String(column.key)} fullWidth margin='normal' error={!!errors[column.key]}>
                                <InputLabel
                                    id={`${String(column.key)}-label`}
                                    shrink
                                    sx={{
                                        backgroundColor: 'white',
                                        padding: '0 4px',
                                        transform: 'translate(14px, -6px) scale(0.75)',
                                        color: errors[column.key] ? '#d32f2f' : 'inherit'
                                    }}
                                >
                                    {defaultLabel(String(column.key))}
                                </InputLabel>
                                <Select
                                    native
                                    error={!!errors[column.key]}
                                    labelId={`${String(column.key)}-label`}
                                    value={newObject[column.key] || ''}
                                    onChange={(e) => handleInputChange(column.key, e.target.value as string)}
                                    label={defaultLabel(String(column.key))}
                                    inputProps={{
                                        'aria-labelledby': `${String(column.key)}-label`
                                    }}
                                >
                                    <option aria-label='None' value='' />
                                    <option value='MALE'>
                                        <Trans>Male</Trans>
                                    </option>
                                    <option value='FEMALE'>
                                        <Trans>Female</Trans>
                                    </option>
                                    <option value='UNKNOWN'>
                                        <Trans>Unknown</Trans>
                                    </option>
                                </Select>
                                {errors[column.key] && <FormHelperText>{errors[column.key]}</FormHelperText>}
                            </FormControl>
                        )
                    } else if (column.type === 'tel') {
                        return (
                            <TextField
                                key={String(column.key)}
                                label={column.label || defaultLabel(String(column.key))}
                                fullWidth
                                margin='normal'
                                value={newObject[column.key] || ''}
                                onChange={(e) => handleInputChange(column.key, formatPhoneNumber(e.target.value))}
                                onFocus={() => setFocusedField(column.key)}
                                onBlur={() => setFocusedField(null)}
                                type='text'
                                error={!!errors[column.key]}
                                helperText={errors[column.key]}
                                variant='outlined'
                                placeholder='123-456-7890'
                                sx={{
                                    width: '100%',
                                    mb: errors[column.key] ? 4 : 2,
                                    '& .MuiFormHelperText-root': {
                                        marginLeft: 0,
                                        marginTop: 1
                                    }
                                }}
                                InputLabelProps={{
                                    shrink: Boolean(newObject[column.key]) || focusedField === column.key,
                                    sx: {
                                        backgroundColor: 'white',
                                        px: 0.5
                                    }
                                }}
                            />
                        )
                    } else {
                        return (
                            <TextField
                                key={String(column.key)}
                                label={defaultLabel(String(column.key))}
                                fullWidth
                                margin='normal'
                                onChange={(e) => handleInputChange(column.key, e.target.value)}
                                type={column.type}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors[column.key]}
                                helperText={errors[column.key]}
                                variant='outlined'
                            />
                        )
                    }
                })}
        </DialogContent>
        <DialogActions className='bg-gray-50 border-t border-gray-200 p-3'>
            <Button onClick={onClose} className='bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-1.5 rounded-md shadow-sm'>
                <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleCreate} className='bg-primary-main text-white hover:bg-primary-dark px-4 py-1.5 rounded-md shadow-sm'>
                <Trans>Create</Trans>
            </Button>
        </DialogActions>
    </Dialog>
))

CreateDialog.displayName = 'CreateDialog'

// Helper function for snake case conversion
const toSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

// Helper function to format phone numbers as user types
const formatPhoneNumber = (phone: string) => {
    let digits = phone.replace(/\D/g, '')

    if (digits.length > 10) {
        digits = digits.substring(0, 10)
    }

    if (digits.length === 10) {
        return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
    } else if (digits.length > 6) {
        return digits.replace(/(\d{3})(\d{3})/, '$1-$2-')
    } else if (digits.length > 3) {
        return digits.replace(/(\d{3})/, '$1-')
    }
    return phone
}

// Create a memoized component for the data section
const DataSection = React.memo<{
    response: { data: any[]; meta: { totalItems: number } } | null
    loading: boolean
    error: string | null
    page: number
    limit: number
    sort: string
    orientation: 'vertical' | 'standard'
    columns: Column<any>[]
    customColumns?: CustomColumn<any>[]
    iconResolver?: (item: any) => JSX.Element
    detailPageRoute?: (id: string) => string
    actions?: { label: string; icon?: React.ReactNode; onClick: (item: any) => void }[]
    iconTitle?: string
    title: string
    singularTitle?: string
    handlePageChange: (event: unknown, newPage: number) => void
    handleLimitChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleSortChange: (property: string) => void
    refetch: () => void
}>(
    ({
        response,
        loading,
        error,
        page,
        limit,
        sort,
        orientation,
        columns,
        customColumns,
        iconResolver,
        detailPageRoute,
        actions,
        iconTitle,
        title,
        singularTitle,
        handlePageChange,
        handleLimitChange,
        handleSortChange,
        refetch
    }) => {
        const history = useHistory()

        // Create a key that changes when data changes
        const dataKey = response ? response.data.map((item) => `${item.id}-${item.status}`).join(',') : 'empty'

        const handleRowClick = useCallback(
            (item: any) => {
                const id = 'id' in item ? item.id : 'taskId' in item ? item.taskId : null
                if (id && detailPageRoute) {
                    history.push(detailPageRoute(String(id)), {
                        paginationState: {
                            page,
                            limit,
                            sort
                        }
                    })
                }
            },
            [detailPageRoute, history, limit, page, sort]
        )

        if (loading) {
            return <LoadingView />
        }

        return (
            <MemoizedDataView
                key={dataKey}
                response={response}
                orientation={orientation}
                columns={columns}
                customColumns={customColumns}
                iconResolver={iconResolver}
                handleRowClick={handleRowClick}
                page={page}
                limit={limit}
                handlePageChange={handlePageChange}
                handleRowsPerPageChange={handleLimitChange}
                sort={sort}
                handleSortChange={handleSortChange}
                disableSort={(key) => {
                    const column = columns.find((col) => col.key === key)
                    return column?.disableSort === true
                }}
                actions={actions}
                iconTitle={iconTitle}
                singularTitle={singularTitle}
                title={title}
                refetch={refetch}
            />
        )
    },
    (prevProps, nextProps) => {
        // Compare only essential props
        return (
            prevProps.loading === nextProps.loading &&
            prevProps.error === nextProps.error &&
            prevProps.page === nextProps.page &&
            prevProps.limit === nextProps.limit &&
            prevProps.sort === nextProps.sort &&
            // Remove refetch from comparison
            JSON.stringify(prevProps.response?.data) === JSON.stringify(nextProps.response?.data)
        )
    }
)

DataSection.displayName = 'DataSection'

export const GenericDataPage = <T extends { [key in K]: string | boolean }, K extends ValidColumn<T>, Q = {}>({
    useData,
    useDataArgs = {},
    columns,
    customColumns,
    title,
    detailPageRoute,
    orientation = 'standard',
    iconResolver,
    iconTitle,
    actions,
    filterButtons,
    singularTitle,
    canCreate = true,
    organizationContext,
    additionalActionButtons
}: GenericDataPageProps<T, K, Q>) => {
    const { response, error, page, setPage, limit, setLimit, sort, setSort, create, loading, refetch, filters, setSearch, setFilters, search } =
        useData()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [newObject, setNewObject] = useState<Partial<T>>({})
    const [errors, setErrors] = useState<{ [key in keyof T]?: string }>({})
    const [focusedField, setFocusedField] = useState<keyof T | null>(null)

    const handleDialogOpen = useCallback(() => {
        setDialogOpen(true)
        // Set default values for new candidate
        setNewObject({
            languagePreference: 'en',
            smsConsent: false
        } as any)
    }, [])
    const handleDialogClose = useCallback(() => {
        setDialogOpen(false)
        setNewObject({})
        setErrors({})
        setFocusedField(null)
    }, [])

    const handleInputChange = useCallback((key: keyof T, value: string | boolean) => {
        setNewObject((prev) => ({ ...prev, [key]: value }))
        setErrors((prev) => ({ ...prev, [key]: '' }))
    }, [])

    const validate = useCallback((): boolean => {
        const newErrors: { [key in keyof T]?: string } = {}
        columns
            .filter((column) => column.editable)
            .forEach((column) => {
                const value = newObject[column.key as keyof T]
                let error = ''

                if (column.type === 'date' && isNaN(Date.parse(value as string))) {
                    if ((!column.required && value) || column.required) {
                        error = t`Invalid date format`
                    }
                } else if (column.type === 'checkbox' && column.required && !value) {
                    error = t`This field is required` // For SMS consent
                } else if (!value && column.required) {
                    error = t`This field is required`
                } else if (column.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value as string)) {
                    if ((!column.required && value) || column.required) {
                        error = t`Invalid email format`
                    }
                } else if (column.type === 'tel' && !/^\d{3}-\d{3}-\d{4}$/.test(value as string)) {
                    if ((!column.required && value) || column.required) {
                        error = t`Invalid phone number format`
                    }
                }

                if (error) {
                    newErrors[column.key as keyof T] = error
                }
            })
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [columns, newObject])

    const handleCreate = useCallback(async () => {
        if (!validate() || !create) return

        try {
            // Add required backend fields that aren't in the form
            const submissionData = {
                ...newObject,
                referrerId: organizationContext?.referrerId || 'manual-entry', // Use organization context or default
                organizationId: organizationContext?.organizationId || 'org_default' // Use organization context or default
            }

            await create(submissionData as Q)
            handleDialogClose()
            refetch?.() // Refresh the data after successful creation
        } catch (error: any) {
            setErrors((prev) => ({
                ...prev,
                [error.key]: error.message || t`An error occurred while creating the item`
            }))
        }
    }, [create, handleDialogClose, newObject, validate, refetch, organizationContext])

    const handlePageChange = useCallback(
        (event: unknown, newPage: number) => {
            setPage(newPage + 1)
        },
        [setPage]
    )

    const handleLimitChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setLimit(parseInt(event.target.value, 10))
            setPage(1)
        },
        [setLimit, setPage]
    )

    const handleSortChange = useCallback(
        (property: keyof T) => {
            const isAsc = sort === `${toSnakeCase(String(property))}:asc`
            setSort(`${toSnakeCase(String(property))}:${isAsc ? 'desc' : 'asc'}`)
        },
        [sort, setSort]
    )

    // Memoize the header component
    const headerComponent = useMemo(
        () => (
            <HeaderSection
                title={title}
                singularTitle={singularTitle}
                totalItems={response?.meta.totalItems}
                error={error}
                filterButtons={filterButtons}
                canCreate={canCreate && !!create}
                handleDialogOpen={handleDialogOpen}
                onSearchChange={setSearch}
                currentSearch={search}
                onFilterChange={setFilters}
                currentFilters={filters || {}}
                additionalActionButtons={additionalActionButtons}
            />
        ),
        [
            title,
            singularTitle,
            response?.meta.totalItems,
            error,
            filterButtons,
            canCreate,
            create,
            handleDialogOpen,
            setSearch,
            search,
            setFilters,
            filters,
            additionalActionButtons
        ]
    )

    return (
        <div className='flex flex-col h-[calc(100vh-100px)]' style={{ overflow: 'hidden' }}>
            <div className='px-8 py-6 flex flex-col h-full' style={{ overflow: 'hidden' }}>
                {headerComponent}
                <div className='flex-grow flex flex-col overflow-hidden'>
                    <DataSection
                        response={response}
                        loading={loading}
                        error={error}
                        page={page}
                        limit={limit}
                        sort={sort}
                        orientation={orientation}
                        columns={columns}
                        customColumns={customColumns}
                        iconResolver={iconResolver}
                        detailPageRoute={detailPageRoute}
                        actions={actions}
                        iconTitle={iconTitle}
                        title={title}
                        singularTitle={singularTitle}
                        handlePageChange={(event: unknown, newPage: number) => handlePageChange(event, newPage)}
                        handleLimitChange={(event: React.ChangeEvent<HTMLInputElement>) => handleLimitChange(event)}
                        handleSortChange={(property: string) => handleSortChange(property as keyof T)}
                        refetch={refetch}
                    />
                </div>
            </div>

            <CreateDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                columns={columns}
                newObject={newObject}
                errors={errors}
                handleInputChange={handleInputChange}
                handleCreate={handleCreate}
                title={title}
                singularTitle={singularTitle}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
            />
        </div>
    )
}

export const getValueFromColumn = <T extends Record<string, unknown>>(item: T, column: Column<T>) => {
    if (String(column.key).split('.').length > 1) {
        const [parentKey, childKey] = String(column.key).split('.')
        return (item[parentKey as keyof T] as Record<string, unknown>)[childKey] as T[keyof T]
    } else {
        return item[column.key as keyof T]
    }
}
